import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { createLead, trackEvent } from "@/lib/api";
import usePageTitle from "@/lib/usePageTitle";

export default function Contact() {
    usePageTitle("Contact — Book a Free Consultation");
    const [form, setForm] = useState({
        name: "", phone: "", email: "", city: "",
        property_type: "Apartment", home_size: "2 BHK",
        message: "", preferred_time: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone) {
            toast.error("Please share your name and phone number.");
            return;
        }
        setSubmitting(true);
        try {
            await createLead({ ...form, source_page: "contact" });
            trackEvent("consultation_submit", { city: form.city, home_size: form.home_size });
            toast.success("Thanks! Our team will call you shortly.");
            setForm({ name: "", phone: "", email: "", city: "", property_type: "Apartment", home_size: "2 BHK", message: "", preferred_time: "" });
        } catch {
            toast.error("Couldn't submit right now. Please try WhatsApp.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="contact-page">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                    <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Contact</div>
                    <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B2E7A] leading-tight" data-testid="contact-title">
                        Let's make your home smarter.
                    </h1>
                    <p className="mt-4 text-slate-600 max-w-md">Free consultation, no pressure. We'll audit your home and share a personalised plan.</p>

                    <div className="mt-8 space-y-4">
                        <a href="tel:+918144814162" className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-[#1BB6F0]/60 transition-colors" data-testid="contact-info-phone">
                            <div className="w-11 h-11 rounded-xl bg-[#E8F8FE] flex items-center justify-center"><Phone className="w-4 h-4 text-[#1BB6F0]" /></div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Phone</div>
                                <div className="text-[#1B2E7A] font-semibold">+91 81448 14162</div>
                            </div>
                        </a>
                        <a href="https://wa.me/918144814162" target="_blank" rel="noreferrer" className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-[#1BB6F0]/60 transition-colors" data-testid="contact-whatsapp">
                            <div className="w-11 h-11 rounded-xl bg-[#E8F8FE] flex items-center justify-center"><MessageCircle className="w-4 h-4 text-[#1BB6F0]" /></div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">WhatsApp</div>
                                <div className="text-[#1B2E7A] font-semibold">Chat with our team</div>
                            </div>
                        </a>
                        <a href="mailto:hello@gosmartnest.com" className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-[#1BB6F0]/60 transition-colors" data-testid="contact-info-email">
                            <div className="w-11 h-11 rounded-xl bg-[#E8F8FE] flex items-center justify-center"><Mail className="w-4 h-4 text-[#1BB6F0]" /></div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</div>
                                <div className="text-[#1B2E7A] font-semibold">hello@gosmartnest.com</div>
                            </div>
                        </a>
                        <div className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200">
                            <div className="w-11 h-11 rounded-xl bg-[#E8F8FE] flex items-center justify-center"><MapPin className="w-4 h-4 text-[#1BB6F0]" /></div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">We serve</div>
                                <div className="text-[#1B2E7A] font-semibold">Kolkata · Gurugram · Bhubaneswar · Agartala</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-10 space-y-5" data-testid="contact-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Name">
                                <Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="contact-name" placeholder="Your full name" />
                            </Field>
                            <Field label="Phone">
                                <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="contact-phone" placeholder="+91" />
                            </Field>
                            <Field label="Email">
                                <Input value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="contact-email" placeholder="you@example.com" />
                            </Field>
                            <Field label="City">
                                <Input value={form.city} onChange={(e) => upd("city", e.target.value)} data-testid="contact-city" placeholder="Kolkata, Gurugram…" />
                            </Field>
                            <Field label="Property type">
                                <Select value={form.property_type} onValueChange={(v) => upd("property_type", v)}>
                                    <SelectTrigger data-testid="contact-property"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["Apartment", "Villa", "Independent House", "Office", "Other"].map((p) => (
                                            <SelectItem key={p} value={p}>{p}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Home size">
                                <Select value={form.home_size} onValueChange={(v) => upd("home_size", v)}>
                                    <SelectTrigger data-testid="contact-size"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK", "Custom"].map((p) => (
                                            <SelectItem key={p} value={p}>{p}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>
                        <Field label="Preferred consultation time">
                            <Input value={form.preferred_time} onChange={(e) => upd("preferred_time", e.target.value)} data-testid="contact-time" placeholder="e.g. Sat afternoon" />
                        </Field>
                        <Field label="What would you like to automate?">
                            <Textarea value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="contact-message" placeholder="Lights, AC, curtains…" className="min-h-28" />
                        </Field>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit"
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1B2E7A] hover:bg-[#13215A] disabled:opacity-60 text-white rounded-full px-8 py-3.5 font-medium transition-colors"
                        >
                            {submitting ? "Sending…" : (<>Book Free Consultation <ArrowRight className="w-4 h-4" /></>)}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-[#1B2E7A] tracking-wide">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}
